import React, { useState } from "react";
import axios from "axios";
import { API, colors } from "../shared/constants";
import { styles, inputStyle, labelStyle, fieldStyle } from "../shared/styles";

const emptyContact = {
  name: "",
  position: "",
  role: "other",
  phone: "",
  mobile: "",
  email: "",
  address: "",
  notes: "",
  receives_reports: false,
  is_scheduling_contact: false,
};

export function AccountFormModal({ onClose, onSave, existingAccount }) {
  const isEdit = !!existingAccount;
  const [step, setStep] = useState(1);
  const [accountData, setAccountData] = useState(
    isEdit
      ? {
          account_name: existingAccount.account_name,
          tier: existingAccount.tier,
          status: existingAccount.status,
        }
      : { account_name: "", tier: "bronze", status: "active" },
  );
  const [contacts, setContacts] = useState(
    isEdit ? existingAccount.contacts.map((c) => ({ ...c })) : [],
  );
  const [editingContactId, setEditingContactId] = useState(null);
  const [currentContact, setCurrentContact] = useState({ ...emptyContact });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const handleContactClick = (c) => {
    setEditingContactId(c.id);
    setCurrentContact({ ...c });
  };
  const handleAddOrUpdateContact = () => {
    if (!currentContact.name.trim()) return;
    if (editingContactId !== null) {
      setContacts(
        contacts.map((c) =>
          c.id === editingContactId
            ? { ...currentContact, id: editingContactId }
            : c,
        ),
      );
      setEditingContactId(null);
    } else {
      setContacts([...contacts, { ...currentContact, id: Date.now() }]);
    }
    setCurrentContact({ ...emptyContact });
  };
  const removeContact = (id) => {
    setContacts(contacts.filter((c) => c.id !== id));
    if (editingContactId === id) {
      setEditingContactId(null);
      setCurrentContact({ ...emptyContact });
    }
  };
  const cancelEdit = () => {
    setEditingContactId(null);
    setCurrentContact({ ...emptyContact });
  };
  const handleSave = async () => {
    if (!accountData.account_name.trim()) {
      setError("Account name is required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      let accountId;
      if (isEdit) {
        await axios.patch(
          `${API}/accounts/${existingAccount.id}/`,
          accountData,
        );
        accountId = existingAccount.id;
        for (const c of existingAccount.contacts) {
          await axios.delete(`${API}/account-contacts/${c.id}/`);
        }
      } else {
        const accRes = await axios.post(`${API}/accounts/`, accountData);
        accountId = accRes.data.id;
      }
      for (const contact of contacts) {
        const { id, ...contactData } = contact;
        await axios.post(`${API}/account-contacts/`, {
          ...contactData,
          account: accountId,
        });
      }
      onSave();
      onClose();
    } catch (e) {
      setError("Something went wrong. Please try again.");
      setSaving(false);
    }
  };
  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        style={{
          backgroundColor: colors.white,
          borderRadius: "12px",
          width: "600px",
          maxHeight: "90vh",
          overflow: "auto",
          boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
        }}
      >
        <div
          style={{
            padding: "20px 24px",
            borderBottom: `1px solid ${colors.border}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ fontSize: "18px", fontWeight: "700" }}>
            {isEdit
              ? `Edit: ${existingAccount.account_name}`
              : step === 1
                ? "New Customer Account"
                : "Add Contacts"}
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: "20px",
              cursor: "pointer",
              color: "#999",
            }}
          >
            ✕
          </button>
        </div>
        <div
          style={{
            display: "flex",
            padding: "16px 24px",
            gap: "8px",
            borderBottom: `1px solid ${colors.border}`,
          }}
        >
          {["Account Details", "Contacts"].map((label, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                opacity: step === i + 1 ? 1 : 0.4,
              }}
            >
              <div
                style={{
                  width: "24px",
                  height: "24px",
                  borderRadius: "50%",
                  backgroundColor: step === i + 1 ? colors.red : "#ddd",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "12px",
                  fontWeight: "700",
                }}
              >
                {i + 1}
              </div>
              <span style={{ fontSize: "13px", fontWeight: "600" }}>
                {label}
              </span>
              {i === 0 && (
                <span style={{ color: "#ddd", marginLeft: "4px" }}>→</span>
              )}
            </div>
          ))}
        </div>
        <div style={{ padding: "24px" }}>
          {step === 1 && (
            <div>
              <div style={fieldStyle}>
                <label style={labelStyle}>Account Name *</label>
                <input
                  style={inputStyle}
                  value={accountData.account_name}
                  onChange={(e) =>
                    setAccountData({
                      ...accountData,
                      account_name: e.target.value,
                    })
                  }
                  placeholder="Enter account name"
                />
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "16px",
                }}
              >
                <div style={fieldStyle}>
                  <label style={labelStyle}>Tier</label>
                  <select
                    style={inputStyle}
                    value={accountData.tier}
                    onChange={(e) =>
                      setAccountData({ ...accountData, tier: e.target.value })
                    }
                  >
                    <option value="vip">VIP</option>
                    <option value="gold">Gold</option>
                    <option value="silver">Silver</option>
                    <option value="bronze">Bronze</option>
                    <option value="brown">Brown</option>
                  </select>
                </div>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Status</label>
                  <select
                    style={inputStyle}
                    value={accountData.status}
                    onChange={(e) =>
                      setAccountData({ ...accountData, status: e.target.value })
                    }
                  >
                    <option value="active">Active</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
              </div>
              {error && (
                <div
                  style={{
                    color: "red",
                    fontSize: "13px",
                    marginBottom: "12px",
                  }}
                >
                  {error}
                </div>
              )}
            </div>
          )}
          {step === 2 && (
            <div>
              {contacts.length > 0 && (
                <div style={{ marginBottom: "20px" }}>
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: "600",
                      marginBottom: "8px",
                      color: "#666",
                    }}
                  >
                    CONTACTS ({contacts.length})
                  </div>
                  {contacts.map((c) => (
                    <div
                      key={c.id}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "10px 14px",
                        backgroundColor:
                          editingContactId === c.id
                            ? "#eaf4fb"
                            : colors.lightGrey,
                        borderRadius: "6px",
                        marginBottom: "6px",
                        border:
                          editingContactId === c.id
                            ? `1px solid ${colors.blue}`
                            : "1px solid transparent",
                      }}
                    >
                      <div
                        style={{ cursor: "pointer" }}
                        onClick={() => handleContactClick(c)}
                      >
                        <span style={{ fontWeight: "600", color: colors.blue }}>
                          {c.name}
                        </span>
                        {c.position && (
                          <span
                            style={{
                              color: "#999",
                              fontSize: "12px",
                              marginLeft: "8px",
                            }}
                          >
                            {c.position}
                          </span>
                        )}
                        <span
                          style={{
                            color: "#aaa",
                            fontSize: "12px",
                            marginLeft: "8px",
                          }}
                        >
                          — {c.role}
                        </span>
                        {editingContactId !== c.id && (
                          <span
                            style={{
                              color: colors.blue,
                              fontSize: "12px",
                              marginLeft: "12px",
                            }}
                          >
                            ✏ click to edit
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => removeContact(c.id)}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#e74c3c",
                          cursor: "pointer",
                          fontSize: "16px",
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div
                style={{
                  padding: "16px",
                  backgroundColor: colors.lightGrey,
                  borderRadius: "8px",
                  marginBottom: "16px",
                  border:
                    editingContactId !== null
                      ? `2px solid ${colors.blue}`
                      : "2px solid transparent",
                }}
              >
                <div
                  style={{
                    fontSize: "13px",
                    fontWeight: "600",
                    marginBottom: "12px",
                    color: editingContactId !== null ? colors.blue : "#666",
                  }}
                >
                  {editingContactId !== null
                    ? "EDITING CONTACT"
                    : "ADD A CONTACT"}
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "12px",
                  }}
                >
                  <div>
                    <label style={labelStyle}>Name</label>
                    <input
                      style={inputStyle}
                      value={currentContact.name}
                      onChange={(e) =>
                        setCurrentContact({
                          ...currentContact,
                          name: e.target.value,
                        })
                      }
                      placeholder="Full name"
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Position</label>
                    <input
                      style={inputStyle}
                      value={currentContact.position}
                      onChange={(e) =>
                        setCurrentContact({
                          ...currentContact,
                          position: e.target.value,
                        })
                      }
                      placeholder="Job title"
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Primary Role</label>
                    <select
                      style={inputStyle}
                      value={currentContact.role}
                      onChange={(e) =>
                        setCurrentContact({
                          ...currentContact,
                          role: e.target.value,
                        })
                      }
                    >
                      <option value="financial">Financial Control</option>
                      <option value="reports">Reports Receiving</option>
                      <option value="scheduling">Scheduling</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Phone</label>
                    <input
                      style={inputStyle}
                      value={currentContact.phone}
                      onChange={(e) =>
                        setCurrentContact({
                          ...currentContact,
                          phone: e.target.value,
                        })
                      }
                      placeholder="Phone number"
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Mobile</label>
                    <input
                      style={inputStyle}
                      value={currentContact.mobile}
                      onChange={(e) =>
                        setCurrentContact({
                          ...currentContact,
                          mobile: e.target.value,
                        })
                      }
                      placeholder="Mobile number"
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Email</label>
                    <input
                      style={inputStyle}
                      value={currentContact.email}
                      onChange={(e) =>
                        setCurrentContact({
                          ...currentContact,
                          email: e.target.value,
                        })
                      }
                      placeholder="Email address"
                    />
                  </div>
                </div>
                <div style={{ marginTop: "12px" }}>
                  <label style={labelStyle}>Address</label>
                  <input
                    style={inputStyle}
                    value={currentContact.address}
                    onChange={(e) =>
                      setCurrentContact({
                        ...currentContact,
                        address: e.target.value,
                      })
                    }
                    placeholder="Address"
                  />
                </div>
                <div style={{ marginTop: "12px" }}>
                  <label style={labelStyle}>Notes</label>
                  <textarea
                    style={{
                      ...inputStyle,
                      height: "70px",
                      resize: "vertical",
                    }}
                    value={currentContact.notes}
                    onChange={(e) =>
                      setCurrentContact({
                        ...currentContact,
                        notes: e.target.value,
                      })
                    }
                    placeholder="Any notes..."
                  />
                </div>
                <div
                  style={{ display: "flex", gap: "16px", marginTop: "12px" }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      fontSize: "13px",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={currentContact.receives_reports}
                      onChange={(e) =>
                        setCurrentContact({
                          ...currentContact,
                          receives_reports: e.target.checked,
                        })
                      }
                    />
                    Receives Reports
                  </label>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      fontSize: "13px",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={currentContact.is_scheduling_contact}
                      onChange={(e) =>
                        setCurrentContact({
                          ...currentContact,
                          is_scheduling_contact: e.target.checked,
                        })
                      }
                    />
                    Scheduling Contact
                  </label>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginTop: "12px",
                  }}
                >
                  <div style={{ display: "flex", gap: "10px" }}>
                    <button
                      onClick={handleAddOrUpdateContact}
                      style={{ ...styles.blueButton }}
                    >
                      {editingContactId !== null
                        ? "Update Contact"
                        : "+ Add Contact"}
                    </button>
                    {editingContactId !== null && (
                      <button
                        onClick={cancelEdit}
                        style={{
                          backgroundColor: "#fff",
                          color: "#666",
                          border: `1px solid ${colors.border}`,
                          padding: "8px 16px",
                          borderRadius: "6px",
                          fontSize: "13px",
                          cursor: "pointer",
                        }}
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    style={{ ...styles.blueButton, padding: "8px 20px" }}
                  >
                    {saving
                      ? "Saving..."
                      : isEdit
                        ? "Save Changes"
                        : "Save Account"}
                  </button>
                </div>
              </div>
              {error && (
                <div
                  style={{
                    color: "red",
                    fontSize: "13px",
                    marginBottom: "12px",
                  }}
                >
                  {error}
                </div>
              )}
            </div>
          )}
        </div>
        <div
          style={{
            padding: "16px 24px",
            borderTop: `1px solid ${colors.border}`,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <button
            onClick={step === 1 ? onClose : () => setStep(1)}
            style={{
              background: "none",
              border: `1px solid ${colors.border}`,
              borderRadius: "6px",
              padding: "8px 20px",
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            {step === 1 ? "Cancel" : "← Back"}
          </button>
          {step === 1 && (
            <button
              onClick={() => {
                if (!accountData.account_name.trim()) {
                  setError("Account name is required.");
                  return;
                }
                setError("");
                setStep(2);
              }}
              style={styles.addButton}
            >
              Next: Contacts →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
